/**
 * Settings Page
 * Application settings and preferences with sidebar navigation
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { LogoutConfirmationModal } from "@/components/modals/logoutConfirmationModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard"
import { useTranslation } from "@/language"
import { AccountPrivacySection } from "../components/AccountPrivacySection"
import { ChangeEmailSection } from "../components/ChangeEmailSection"
import { ChangePhoneSection } from "../components/ChangePhoneSection"
import { ConnectedAccountsSection } from "../components/ConnectedAccountsSection"
import { HelpCenterSection } from "../components/HelpCenterSection"
import { InviteFriendsSection } from "../components/InviteFriendsSection"
import { LanguageSection } from "../components/LanguageSection"
import type { NotificationsSectionHandle } from "../components/NotificationsSection"
import { NotificationsSection } from "../components/NotificationsSection"
import { PasswordSecuritySection } from "../components/PasswordSecuritySection"
import { PaymentsEarningsSection } from "../components/PaymentsEarningsSection"
import { PrivacyPolicySection } from "../components/PrivacyPolicySection"
import { RemoveAccountSection } from "../components/RemoveAccountSection"
import { SettingsSidebar } from "../components/SettingsSidebar"
import { SubscriptionSection } from "../components/SubscriptionSection"
import { TimeZoneSection } from "../components/TimeZoneSection"
import { UnsavedChangesModal } from "../components/UnsavedChangesModal"
import type { SettingsSection } from "../types/settings.types"

const SECTION_COMPONENTS: Record<SettingsSection, React.ComponentType> = {
  "password-security": PasswordSecuritySection,
  "change-phone": ChangePhoneSection,
  "change-email": ChangeEmailSection,
  "account-privacy": AccountPrivacySection,
  "connected-accounts": ConnectedAccountsSection,
  subscription: SubscriptionSection,
  "payments-earnings": PaymentsEarningsSection,
  "invite-friends": InviteFriendsSection,
  notifications: NotificationsSection,
  language: LanguageSection,
  timezone: TimeZoneSection,
  "help-center": HelpCenterSection,
  "privacy-policy": PrivacyPolicySection,
  "terms-and-conditions": PrivacyPolicySection,
  "remove-account": RemoveAccountSection,
}

function getSettingsSection(sectionParam: string | null): SettingsSection {
  const validSections: SettingsSection[] = [
    "password-security",
    "change-phone",
    "change-email",
    "account-privacy",
    "connected-accounts",
    "subscription",
    "payments-earnings",
    "invite-friends",
    "notifications",
    "language",
    "timezone",
    "help-center",
    "privacy-policy",
    "terms-and-conditions",
    "remove-account",
  ]

  return validSections.includes(sectionParam as SettingsSection)
    ? (sectionParam as SettingsSection)
    : "change-phone"
}

function buildSettingsUrl(section: SettingsSection): string {
  return `/settings?section=${section}`
}

export function SettingsScreen() {
  const { t } = useTranslation("settings")
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const logout = useAuthStore(state => state.logout)

  const sectionParam = searchParams.get("section")
  const [currentSectionParam, setCurrentSectionParam] = useState<string | null>(sectionParam)
  // Initialise to a stable default. The useEffect below syncs the initial URL
  // on mount only, so subsequent programmatic setActiveSection calls (e.g. from
  // navigateToSection) are NOT overridden because this effect does NOT re-run.
  const [activeSection, setActiveSection] = useState<SettingsSection>(() =>
    getSettingsSection(sectionParam),
  )
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [pendingSection, setPendingSection] = useState<SettingsSection | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const historyStack = useRef<string[]>([])
  const notificationsSaveRef = useRef<NotificationsSectionHandle | null>(null)

  // Refs for popstate handler (avoids re-subscribing listener on state changes)
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges)
  hasUnsavedChangesRef.current = hasUnsavedChanges
  const activeSectionRef = useRef(activeSection)
  activeSectionRef.current = activeSection

  const handledToastParams = useRef<string>("")

  const unsavedGuard = useUnsavedChangesGuard(hasUnsavedChanges)

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setHasUnsavedChanges(dirty)
  }, [])

  useEffect(() => {
    historyStack.current = JSON.parse(sessionStorage.getItem("settingsNavStack") || "[]")
  }, [])

  // Sync `activeSection` from the URL on mount only.  Subsequent programmatic
  // `setActiveSection` calls (e.g. from navigateToSection) are NOT overridden
  // because this effect does not re-run after the initial render.
  // biome-ignore lint: intentionally mount-only
  useEffect(() => {
    setActiveSection(getSettingsSection(sectionParam))
  }, [])

  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search)
      const newSection = params.get("section")
      const resolvedNewSection = getSettingsSection(newSection)

      // If there are unsaved changes and user is navigating away from
      // the current section (same-pathname query change), push the
      // current state back and show the leave confirmation modal.
      if (hasUnsavedChangesRef.current && resolvedNewSection !== activeSectionRef.current) {
        navigate(buildSettingsUrl(activeSectionRef.current), { replace: true })
        setPendingSection(resolvedNewSection)
        return
      }

      const stack = historyStack.current
      if (!newSection || stack[stack.length - 1] !== newSection) {
        historyStack.current = stack.slice(0, -1)
        sessionStorage.setItem("settingsNavStack", JSON.stringify(historyStack.current))
      }

      setCurrentSectionParam(newSection)
    }
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  }, [navigate])

  useEffect(() => {
    setCurrentSectionParam(sectionParam)
  }, [sectionParam])

  useEffect(() => {
    const oauthSuccess = searchParams.get("oauth")
    const oauthError = searchParams.get("oauth_error")
    const provider = searchParams.get("provider")
    const requestedSection = getSettingsSection(sectionParam)
    const checkoutSuccess = searchParams.get("success")
    const checkoutCanceled = searchParams.get("canceled")

    setActiveSection(requestedSection)
    setCurrentSectionParam(sectionParam)

    const toastKey = [oauthSuccess, oauthError, checkoutSuccess, checkoutCanceled]
      .filter(Boolean)
      .join(":")
    if (!toastKey || handledToastParams.current === toastKey) return

    if (checkoutSuccess === "true") {
      toast.success(t("subscription.checkoutSuccess"))
      setActiveSection("subscription")
      setCurrentSectionParam("subscription")
      navigate(buildSettingsUrl("subscription"), { replace: true })
    }

    if (checkoutCanceled === "true") {
      toast.info(t("subscription.checkoutCanceled"))
      setActiveSection("subscription")
      setCurrentSectionParam("subscription")
      navigate(buildSettingsUrl("subscription"), { replace: true })
    }

    if (oauthSuccess === "success") {
      toast.success(t("connectedAccounts.connectedSuccess", { provider }))
      setCurrentSectionParam(requestedSection)
      navigate(buildSettingsUrl(requestedSection), { replace: true })
    }

    if (oauthError) {
      if (oauthError === "access_denied") {
        setActiveSection("connected-accounts")
        setCurrentSectionParam("connected-accounts")
        toast.error(
          "Calendar access is required to connect Google. Please allow access and try again.",
        )
      } else {
        toast.error(`OAuth error: ${oauthError}`)
      }
      navigate(buildSettingsUrl("connected-accounts"), { replace: true })
    }

    handledToastParams.current = toastKey
  }, [searchParams, t, sectionParam, navigate])

  const handleSectionChange = (section: SettingsSection) => {
    if (section === activeSection) return

    // If there are unsaved changes, show the leave confirmation modal
    if (hasUnsavedChanges) {
      setPendingSection(section)
      return
    }

    navigateToSection(section)
  }

  const navigateToSection = (section: SettingsSection) => {
    setActiveSection(section)
    setCurrentSectionParam(section)
    setHasUnsavedChanges(false)
    navigate(buildSettingsUrl(section))
    historyStack.current = [...historyStack.current, section]
    sessionStorage.setItem("settingsNavStack", JSON.stringify(historyStack.current))
  }

  const handleSaveAndLeave = async () => {
    setIsSaving(true)
    try {
      await notificationsSaveRef.current?.save()
      if (pendingSection) {
        unsavedGuard.allowNavigation()
        navigateToSection(pendingSection)
        setPendingSection(null)
      } else {
        unsavedGuard.allowNavigation()
        unsavedGuard.confirmLeave()
      }
    } catch {
      // Save failed – stay on the page so the user can try again
    } finally {
      setIsSaving(false)
    }
  }

  const handleLeaveWithoutSaving = () => {
    if (pendingSection) {
      unsavedGuard.allowNavigation()
      navigateToSection(pendingSection)
      setPendingSection(null)
    } else {
      unsavedGuard.confirmLeave()
    }
  }

  const handleCancelLeave = () => {
    // "Leave Anyway" discards changes and navigates to the pending section.
    if (pendingSection) {
      unsavedGuard.allowNavigation()
      navigateToSection(pendingSection)
      setPendingSection(null)
    } else {
      unsavedGuard.cancelLeave()
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      setShowLogoutModal(false)
    } catch (error) {
      console.error("Logout error:", error)
    }
  }

  const isDetail = !!currentSectionParam

  const renderActiveSection = () => {
    const SectionComponent = SECTION_COMPONENTS[activeSection]
    if (activeSection === "notifications") {
      const NotificationsComp = SectionComponent as typeof NotificationsSection
      return <NotificationsComp onDirtyChange={handleDirtyChange} saveRef={notificationsSaveRef} />
    }
    return <SectionComponent />
  }

  return (
    <div className="bg-background min-h-[calc(100vh-100px)] lg:min-h-[calc(100vh-150px)] relative lg:px-17.5">
      <div className="max-w-360 mx-auto lg:mt-[50px] border-0 lg:border-2 border-card rounded-lg">
        <div className="flex">
          {/* Desktop: Sidebar */}
          <aside className="hidden lg:block w-94 shrink-0 bg-card py-5 h-[calc(100vh-150px)] overflow-y-auto rounded-tl-lg rounded-bl-lg">
            <SettingsSidebar
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              onLogout={() => setShowLogoutModal(true)}
            />
          </aside>

          {/* Mobile: list view (sidebar only) */}
          {!isDetail && (
            <aside className="flex-1 lg:hidden overflow-y-auto min-h-[calc(100vh-2.5rem)]">
              <SettingsSidebar
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
                onLogout={() => setShowLogoutModal(true)}
              />
            </aside>
          )}

          {/* Mobile: detail view (content only) */}
          {isDetail && (
            <main className="flex-1 lg:hidden px-5 pb-2.5 md:pt-5 lg:pt-0 lg:bg-[#FFFFFF0D] h-[calc(100vh-74px)] rounded-lg overflow-y-auto">
              {renderActiveSection()}
            </main>
          )}

          {/* Desktop: Content */}
          <main className="hidden lg:flex flex-1 min-w-0 p-7.5 pb-0 bg-[#FFFFFF0D] rounded-tr-lg rounded-br-lg h-[calc(100vh-150px)] overflow-y-scroll">
            {renderActiveSection()}
          </main>
        </div>
      </div>

      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />

      <UnsavedChangesModal
        isOpen={unsavedGuard.isBlocked || pendingSection !== null}
        onSaveAndLeave={handleSaveAndLeave}
        onLeaveWithoutSaving={handleLeaveWithoutSaving}
        onCancel={handleCancelLeave}
        isSaving={isSaving}
      />
    </div>
  )
}
